import { useCallback, useEffect, useMemo, useState } from "react";

const limits = [
    8,
    12,
    16,
    25,
    50,
    75,
    100,
    200,
    300,
    400,
    500,
    750,
    1000,
];

export type PaginationMode = "local" | "remote";

export interface PaginationOptions {
    mode?: PaginationMode;
    defaultOffset?: number;
    defaultLimit?: number;
    localTotal?: number;
    remoteTotal?: number;
}

export interface Pagination {
    limits: number[];
    mode: PaginationMode;
    localTotal: number;
    remoteTotal: number;
    offset: number;
    limit: number;
    page: number;
    searchParams: Record<string, string | number | boolean>;
    setMode(mode: PaginationMode);
    setLocalTotal(localTotal: number);
    setRemoteTotal(remoteTotal: number);
    setOffset(offset: number);
    setLimit(limit: number);
    setPage(page: number);
    previousPage();
    nextPage();
}

export const usePagination = (options: PaginationOptions = {}): Pagination => {
    const [mode, setMode] = useState<PaginationMode>(options?.mode || "local");
    const [localTotal, setLocalTotal] = useState(0);
    const [remoteTotal, setRemoteTotal] = useState(0);

    useEffect(() => {
        setMode(mode => options?.mode || mode);
    }, [options?.mode]);

    useEffect(() => {
        setLocalTotal(options?.localTotal || 0);
    }, [options?.localTotal]);

    useEffect(() => {
        setRemoteTotal(options?.remoteTotal || 0);
    }, [options?.remoteTotal]);

    const [offset, _setOffset] = useState(
        options.defaultOffset > 0 ? Math.floor(options.defaultOffset) : 0,
    );

    const [limit, _setLimit] = useState(
        options.defaultLimit > 0 ? Math.ceil(options.defaultLimit) : 16,
    );

    const setOffset = useCallback((offset: number) => {
        _setOffset(offset > 0 ? Math.floor(offset) : 0);
    }, []);

    const setLimit = useCallback((limit: number) => {
        _setLimit(currentLimit => (limit ? Math.ceil(limit) : currentLimit));
        _setOffset(0);
    }, []);

    const page = useMemo(() => {
        return Math.floor(offset / limit) + 1;
    }, [limit, offset]);

    const setPage = useCallback((page: number) => {
        setOffset(Math.ceil(page - 1) * limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit]);

    const previousPage = useCallback(() => {
        setPage(page > 0 ? page - 1 : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const nextPage = useCallback(() => {
        setPage(page + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    return {
        limits,
        mode,
        localTotal,
        remoteTotal,
        offset,
        limit,
        page,
        searchParams: { offset, limit },
        setMode,
        setLocalTotal,
        setRemoteTotal,
        setOffset,
        setLimit,
        setPage,
        previousPage,
        nextPage,
    };
};

export const usePaginatedSlice = <Item>(
    list: Item[],
    pagination: Pagination,
    updateKey: string = null,
    ignore: boolean = false,
): Item[] => {
    const slice = useMemo(() => {
        if (ignore) {
            return list;
        }

        const slice = [];

        for (let index = 0; index < list?.length; index += 1) {
            if (index < pagination.offset) {
                continue;
            }

            if (index > pagination.offset + pagination.limit - 1) {
                break;
            }

            slice.push(list[index]);
        }

        return slice;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ignore, updateKey, list?.length, pagination.offset, pagination.limit]);

    return slice;
};
